/**
 * WB3-L4 — Central action execution dispatch (handlerRef → UI handlers).
 *
 * Starts consolidation of DynamicTable row dispatch and detail kernel handlers.
 */

import type { NavigateFunction } from 'react-router-dom';
import type { WorkbenchAction, WorkbenchKernelExecutionSurface } from '@shared/business-workbench';
import type { WorkbenchInlineFieldMutation } from '@shared/business-workbench/workbench-interaction-surface-contract';
import type { WorkbenchDragDropMutation } from '@shared/business-workbench/workbench-drag-drop-contract';
import { SELLABLE_GOLDEN_HANDLER_REFS } from '@shared/business-workbench/sellable-action-rule-table';
import {
  buildWorkbenchExecutionResult,
  resolveActionRiskTier,
  type WorkbenchExecutionResult,
} from '@shared/business-workbench/workbench-action-safety';
import type { ObjectRef } from '@shared/business-workbench/workbench-focus';
import type { WorkbenchIntent } from '@shared/business-workbench/workbench-intent';
import {
  isBulkStateChangeHandlerRef,
  isFinanceCompensatingHandlerRef,
  isHighTierHandlerRef,
  requiresPopconfirmGate,
} from '@shared/business-workbench/workbench-action-recoverability';
import type { WorkbenchActionExecutionEntry } from '@shared/business-workbench/workbench-action-execution-log';
import { COLLECTION_DOMAIN_HANDLER_REFS } from '@shared/business-workbench/workbench-domain-action-handler-refs';
import { COLLECTION_CONTACT_FLOW_OPERATOR_LABELS } from '@shared/business-workbench/collection-contact-flow-operator-labels';
import { COLLECTION_COMMITMENT_OPERATOR_LABELS } from '@shared/business-workbench/collection-commitment-operator-labels';
import { SUPPLIER_RECEIVING_DOMAIN_HANDLER_REFS } from '@shared/business-workbench/workbench-domain-action-handler-refs';
import { SUPPLIER_GOODS_RECEIPT_FLOW_OPERATOR_LABELS } from '@shared/business-workbench/supplier-goods-receipt-flow-operator-labels';
import { emitWorkbenchAuxiliaryOpenTab } from '../../workbench/workbench-auxiliary-events';
import {
  runWorkbenchAuxiliaryPipeline,
  type WorkbenchAuxiliaryPipelineInput,
} from '../../workbench/workbench-auxiliary-pipeline';
import {
  reportWorkbenchKernelDispatchFailure,
  applyWorkbenchKernelDispatchFeedback,
} from './workbench-kernel-dispatch-feedback';

export function mapWorkbenchActionToHandler(
  action: WorkbenchAction,
  handlers: Record<string, () => void>
): void {
  const handler = handlers[action.handlerRef] ?? handlers[action.id];
  handler?.();
}

/** WB3-L3 — denial reason from v3 `reason` or legacy capability `tooltip`. */
export function resolveWorkbenchActionDenialReason(
  action: Pick<WorkbenchAction, 'reason'> & { tooltip?: string }
): string | undefined {
  return action.reason?.trim() || action.tooltip?.trim() || undefined;
}

export interface DetailKernelHandlerOptions {
  onEdit: () => void;
  onDelete?: () => void;
  onRestore?: () => void;
  onStartFlow?: (flowTemplateId: string) => void;
}

/** K5 detail kernel handler map for `mapWorkbenchActionToHandler`. */
export function buildDetailKernelHandlerMap(
  options: DetailKernelHandlerOptions
): Record<string, () => void> {
  const handlers: Record<string, () => void> = {
    'crud:edit': options.onEdit,
  };

  if (options.onDelete) {
    handlers['crud:delete'] = options.onDelete;
  }
  if (options.onRestore) {
    handlers['crud:restore'] = options.onRestore;
  }

  return handlers;
}

/** Records a kernel action via the auxiliary pipeline (local log → emitWorkbenchActionExecuted → client outbox-first timeline POST). */
export function recordWorkbenchKernelAction(
  input: WorkbenchAuxiliaryPipelineInput
): WorkbenchActionExecutionEntry {
  return runWorkbenchAuxiliaryPipeline(input).entry;
}

function buildKernelHandlerSuccessResult(
  action: Pick<WorkbenchAction, 'id'>,
  deps: WorkbenchKernelDispatchDeps,
  riskPolicy: ReturnType<typeof resolveActionRiskTier>,
  entry: WorkbenchActionExecutionEntry,
  userMessage?: WorkbenchExecutionResult['userMessage']
): WorkbenchExecutionResult {
  return buildWorkbenchExecutionResult({
    ok: true,
    handled: true,
    actionId: action.id,
    objectRef: deps.objectRef,
    riskPolicy,
    ...(entry.timelineEventId ? { timelineEventId: entry.timelineEventId } : {}),
    ...(entry.revisionSnapshotId ? { revisionId: entry.revisionSnapshotId } : {}),
    ...(userMessage ? { userMessage } : {}),
  });
}

export interface WorkbenchKernelDispatchDeps {
  tenantId: string;
  objectRef: ObjectRef;
  workspaceDomain?: string;
  workspaceSubdomain?: string;
  actorUserId?: string;
  navigate?: NavigateFunction;
  onOpenDetail?: () => void;
  onEnterEditMode?: () => void;
  onDuplicate?: () => void;
  onIntentChange?: (intent: WorkbenchIntent) => void;
  onExport?: () => void;
  onStartFlow?: (flowTemplateId: string) => void;
  onOpenCollectionContactFlow?: () => void | Promise<void>;
  onOpenCollectionCommitment?: () => void | Promise<void>;
  onOpenSupplierGoodsReceiptFlow?: () => void | Promise<void>;
  onDelete?: () => void | Promise<void>;
  onFinanceReversal?: (sourceVoucherId: string) => void | Promise<void>;
  onBulkStateChange?: (handlerRef: string) => void | Promise<void>;
  entitySnapshot?: unknown;
  flowStartContext?: unknown;
  selectionCount?: number;
  selectedRefs?: readonly ObjectRef[];
  resolveRecordSnapshot?: (ref: ObjectRef) => unknown;
  surface?: WorkbenchKernelExecutionSurface;
  /** WB4-IX0 — inline_edit surface mutation payload. */
  inlineFieldMutation?: WorkbenchInlineFieldMutation;
  onInlineFieldPersist?: (input: { fieldKey: string; value: string }) => void | Promise<void>;
  /** WB4-DND1 — drag_drop surface mutation payload. */
  dragDropMutation?: WorkbenchDragDropMutation;
  onDragDropAssignTask?: (input: {
    taskId: string;
    assigneeId: string;
    assigneeLabel: string;
  }) => void | Promise<void>;
  onDragDropAttachEvidence?: (input: {
    documentId: string;
    evidencePanelId: string;
    evidencePanelLabel: string;
  }) => void | Promise<void>;
  onDragDropStartProductionFlow?: (input: {
    orderId: string;
    queueId: string;
    queueLabel: string;
  }) => void | Promise<void>;
  onDragDropRouteToPickingQueue?: (input: {
    orderId: string;
    pickingQueueId: string;
    pickingQueueLabel: string;
  }) => void | Promise<void>;
  onDragDropRouteToCollectionQueue?: (input: {
    customerId: string;
    collectionQueueId: string;
    collectionQueueLabel: string;
  }) => void | Promise<void>;
  onDragDropAttachApprovalPacket?: (input: {
    documentId: string;
    approvalPacketId: string;
    approvalPacketLabel: string;
  }) => void | Promise<void>;
  onDragDropCreatePurchaseRequest?: (input: {
    shortageId: string;
    purchaseRequestId: string;
    purchaseRequestLabel: string;
  }) => void | Promise<void>;
  onDragDropLinkDocumentToOrder?: (input: {
    documentId: string;
    orderId: string;
    orderLabel: string;
  }) => void | Promise<void>;
  onDragDropLinkOrderToCustomer?: (input: {
    orderId: string;
    customerId: string;
    customerLabel: string;
  }) => void | Promise<void>;
}

function kernelActionContext(
  deps: WorkbenchKernelDispatchDeps
): Pick<
  Parameters<typeof recordWorkbenchKernelAction>[0],
  'tenantId' | 'objectRef' | 'workspaceDomain' | 'workspaceSubdomain' | 'actorUserId' | 'surface'
> {
  return {
    tenantId: deps.tenantId,
    objectRef: deps.objectRef,
    ...(deps.workspaceDomain ? { workspaceDomain: deps.workspaceDomain } : {}),
    ...(deps.workspaceSubdomain ? { workspaceSubdomain: deps.workspaceSubdomain } : {}),
    ...(deps.actorUserId ? { actorUserId: deps.actorUserId } : {}),
    ...(deps.surface ? { surface: deps.surface } : {}),
  };
}

/** Handlers executed by workbench-gated-kernel-dispatch (not ActionExecutor switch). */
function isExternallyDispatchedKernelHandlerRef(handlerRef: string): boolean {
  return (
    requiresPopconfirmGate(handlerRef) ||
    isHighTierHandlerRef(handlerRef) ||
    isFinanceCompensatingHandlerRef(handlerRef) ||
    isBulkStateChangeHandlerRef(handlerRef)
  );
}

/**
 * P79-6 — centralized handlerRef dispatch for list · inspector · palette · bulk.
 */
export function dispatchWorkbenchKernelHandler(
  action: Pick<WorkbenchAction, 'id' | 'label' | 'handlerRef'>,
  deps: WorkbenchKernelDispatchDeps
): boolean {
  return dispatchWorkbenchKernelHandlerWithResult(action, deps).handled;
}

export function dispatchWorkbenchKernelHandlerWithResult(
  action: Pick<WorkbenchAction, 'id' | 'label' | 'handlerRef'>,
  deps: WorkbenchKernelDispatchDeps
): WorkbenchExecutionResult {
  const riskPolicy = resolveActionRiskTier({
    actionId: action.id,
    handlerRef: action.handlerRef,
    businessDomain: deps.objectRef.businessDomain,
  });

  const { handlerRef } = action;

  if (handlerRef.startsWith('workbench:intent:')) {
    const intent = handlerRef.slice('workbench:intent:'.length) as WorkbenchIntent;
    const entry = recordWorkbenchKernelAction({
      ...kernelActionContext(deps),
      action,
      recoverabilityTier: riskPolicy.riskTier,
    });
    deps.onIntentChange?.(intent);
    return buildKernelHandlerSuccessResult(action, deps, riskPolicy, entry);
  }

  switch (handlerRef) {
    case SELLABLE_GOLDEN_HANDLER_REFS.openDetail:
    case 'crud:view': {
      const entry = recordWorkbenchKernelAction({
        ...kernelActionContext(deps),
        action,
        recoverabilityTier: riskPolicy.riskTier,
      });
      deps.onOpenDetail?.();
      return buildKernelHandlerSuccessResult(action, deps, riskPolicy, entry);
    }
    case SELLABLE_GOLDEN_HANDLER_REFS.editDescription:
    case 'crud:edit': {
      const entry = recordWorkbenchKernelAction({
        ...kernelActionContext(deps),
        action,
        recoverabilityTier: riskPolicy.riskTier,
      });
      deps.onEnterEditMode?.();
      return buildKernelHandlerSuccessResult(action, deps, riskPolicy, entry);
    }
    case 'crud:duplicate': {
      const entry = recordWorkbenchKernelAction({
        ...kernelActionContext(deps),
        action,
        recoverabilityTier: riskPolicy.riskTier,
      });
      deps.onDuplicate?.();
      return buildKernelHandlerSuccessResult(action, deps, riskPolicy, entry);
    }
    case 'workbench:export': {
      const entry = recordWorkbenchKernelAction({
        ...kernelActionContext(deps),
        action,
        recoverabilityTier: riskPolicy.riskTier,
      });
      deps.onExport?.();
      return buildKernelHandlerSuccessResult(action, deps, riskPolicy, entry);
    }
    case SELLABLE_GOLDEN_HANDLER_REFS.viewAudit:
    case 'navigate:audit': {
      const entry = recordWorkbenchKernelAction({
        ...kernelActionContext(deps),
        action,
        recoverabilityTier: riskPolicy.riskTier,
      });
      emitWorkbenchAuxiliaryOpenTab('audit', { expand: true });
      return buildKernelHandlerSuccessResult(action, deps, riskPolicy, entry);
    }
    case SELLABLE_GOLDEN_HANDLER_REFS.viewAuditBulk: {
      const entry = recordWorkbenchKernelAction({
        ...kernelActionContext(deps),
        action,
        recoverabilityTier: riskPolicy.riskTier,
      });
      emitWorkbenchAuxiliaryOpenTab('audit', { expand: true });
      return buildKernelHandlerSuccessResult(action, deps, riskPolicy, entry);
    }
    case 'navigate:related': {
      const entry = recordWorkbenchKernelAction({
        ...kernelActionContext(deps),
        action,
        recoverabilityTier: riskPolicy.riskTier,
      });
      emitWorkbenchAuxiliaryOpenTab('timeline', { expand: true });
      return buildKernelHandlerSuccessResult(action, deps, riskPolicy, entry);
    }
    case COLLECTION_DOMAIN_HANDLER_REFS.openContactFlow: {
      const entry = recordWorkbenchKernelAction({
        ...kernelActionContext(deps),
        action,
        recoverabilityTier: riskPolicy.riskTier,
      });
      if (!deps.onOpenCollectionContactFlow) {
        reportWorkbenchKernelDispatchFailure({
          action,
          reason: 'unwired',
          businessDomain: deps.objectRef.businessDomain,
          userMessage: COLLECTION_CONTACT_FLOW_OPERATOR_LABELS.unwiredHandler,
        });
        return buildWorkbenchExecutionResult({
          ok: false,
          handled: false,
          actionId: action.id,
          objectRef: deps.objectRef,
          riskPolicy,
          userMessage: {
            title: COLLECTION_CONTACT_FLOW_OPERATOR_LABELS.unwiredHandler,
          },
        });
      }
      void Promise.resolve(deps.onOpenCollectionContactFlow()).catch(() => undefined);
      return buildKernelHandlerSuccessResult(action, deps, riskPolicy, entry);
    }
    case COLLECTION_DOMAIN_HANDLER_REFS.recordCommitment: {
      const entry = recordWorkbenchKernelAction({
        ...kernelActionContext(deps),
        action,
        recoverabilityTier: riskPolicy.riskTier,
      });
      if (!deps.onOpenCollectionCommitment) {
        reportWorkbenchKernelDispatchFailure({
          action,
          reason: 'unwired',
          businessDomain: deps.objectRef.businessDomain,
          userMessage: COLLECTION_COMMITMENT_OPERATOR_LABELS.unwiredHandler,
        });
        return buildWorkbenchExecutionResult({
          ok: false,
          handled: false,
          actionId: action.id,
          objectRef: deps.objectRef,
          riskPolicy,
          userMessage: {
            title: COLLECTION_COMMITMENT_OPERATOR_LABELS.unwiredHandler,
          },
        });
      }
      void Promise.resolve(deps.onOpenCollectionCommitment()).catch(() => undefined);
      return buildKernelHandlerSuccessResult(action, deps, riskPolicy, entry);
    }
    case SUPPLIER_RECEIVING_DOMAIN_HANDLER_REFS.openGoodsReceiptFlow: {
      const entry = recordWorkbenchKernelAction({
        ...kernelActionContext(deps),
        action,
        recoverabilityTier: riskPolicy.riskTier,
      });
      if (!deps.onOpenSupplierGoodsReceiptFlow) {
        reportWorkbenchKernelDispatchFailure({
          action,
          reason: 'unwired',
          businessDomain: deps.objectRef.businessDomain,
          userMessage: SUPPLIER_GOODS_RECEIPT_FLOW_OPERATOR_LABELS.unwiredHandler,
        });
        return buildWorkbenchExecutionResult({
          ok: false,
          handled: false,
          actionId: action.id,
          objectRef: deps.objectRef,
          riskPolicy,
          userMessage: {
            title: SUPPLIER_GOODS_RECEIPT_FLOW_OPERATOR_LABELS.unwiredHandler,
          },
        });
      }
      void Promise.resolve(deps.onOpenSupplierGoodsReceiptFlow()).catch(() => undefined);
      return buildKernelHandlerSuccessResult(action, deps, riskPolicy, entry);
    }
    case 'clipboard:copy-id': {
      const entry = recordWorkbenchKernelAction({
        ...kernelActionContext(deps),
        action,
        recoverabilityTier: riskPolicy.riskTier,
      });
      if (typeof navigator !== 'undefined' && navigator.clipboard?.writeText) {
        void navigator.clipboard.writeText(deps.objectRef.id);
      }
      return buildKernelHandlerSuccessResult(action, deps, riskPolicy, entry);
    }
    default: {
      if (isExternallyDispatchedKernelHandlerRef(handlerRef)) {
        reportWorkbenchKernelDispatchFailure({
          action,
          reason: 'misrouted_gated',
          businessDomain: deps.objectRef.businessDomain,
          userMessage: '이 작업은 보호된 실행 경로가 필요합니다.',
        });
      } else {
        reportWorkbenchKernelDispatchFailure({
          action,
          reason: 'unwired',
          businessDomain: deps.objectRef.businessDomain,
        });
      }
      return buildWorkbenchExecutionResult({
        ok: false,
        handled: false,
        actionId: action.id,
        objectRef: deps.objectRef,
        riskPolicy,
        userMessage: {
          title: isExternallyDispatchedKernelHandlerRef(handlerRef)
            ? '이 작업은 보호된 실행 경로가 필요합니다.'
            : '지원하지 않는 작업입니다.',
        },
      });
    }
  }
}

export interface TableRowHandlerOptions<T> {
  onView: (record: T) => void;
  onEdit: (record: T) => void;
  onDelete: (record: T) => void;
  onDuplicate: (record: T) => void;
  onMore: (record: T) => void;
}

/** W2 table row dispatch — delegation entry for DynamicTable progressive actions. */
export function dispatchTableWorkbenchAction<T>(
  action: WorkbenchAction,
  record: T,
  handlers: TableRowHandlerOptions<T>,
  kernelDeps?: WorkbenchKernelDispatchDeps
): void {
  if (kernelDeps) {
    const result = dispatchWorkbenchKernelHandlerWithResult(action, {
      ...kernelDeps,
      surface: kernelDeps.surface ?? 'context_menu',
      onOpenDetail: () => handlers.onView(record),
      onEnterEditMode: () => handlers.onEdit(record),
      onDuplicate: () => handlers.onDuplicate(record),
    });
    if (result.handled) {
      return;
    }
    applyWorkbenchKernelDispatchFeedback(result, action);
  }

  switch (action.handlerRef) {
    case 'crud:view':
      handlers.onView(record);
      return;
    case 'crud:edit':
      handlers.onEdit(record);
      return;
    case 'crud:delete':
      handlers.onDelete(record);
      return;
    case 'crud:duplicate':
      handlers.onDuplicate(record);
      return;
    default:
      handlers.onMore(record);
  }
}
